import { useEffect, useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

/**
 * Wraps a React Query result with an 8-second timeout.
 * If still loading after `timeoutMs`, sets `timedOut = true`.
 * Reset when loading completes or query is refetched.
 *
 * Usage:
 *   const { data, isLoading, timedOut, refetch } = useTimedQuery(useQuery(...));
 *   if (timedOut) return <TimedOutState onRetry={refetch} />;
 */
export function useTimedQuery<T extends Pick<UseQueryResult, 'isLoading' | 'isFetching'>>(
  query: T,
  timeoutMs = 8000
): T & { timedOut: boolean } {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!query.isLoading && !query.isFetching) {
      setTimedOut(false);
      return;
    }
    const t = window.setTimeout(() => setTimedOut(true), timeoutMs);
    return () => window.clearTimeout(t);
  }, [query.isLoading, query.isFetching, timeoutMs]);

  return { ...query, timedOut };
}
